import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { DeliveryMarkDto, DeliveryStatus, KitchenDayDto } from '@tiffinify/contracts';
import { deliveryUpdateMessage, formatDay, mealForTime, mealsOf, tiffinsLabel, waLink, type KitchenCustomer, type Meal } from '@tiffinify/core';
import { PageLoader, Badge, BottomNav, BtnPrimary, BtnSecondary, Card, Chip, Chips, Empty, FoldCard, FoodMark, LoadError, Hero, Icon, Mono, PageTitle, SearchField, Segmented, Sheet, Skel, useConfirm, type IconName } from '../components/ui';
import { put } from '../lib/api';
import { errorMessage, mealCount, mealName } from '../lib/format';
import { useKitchenDay, useMe, useToday } from '../lib/queries';
import { WhatsAppText } from '../lib/whatsapp';

type Mark = DeliveryStatus | null;
interface MarkVars { day: string; meal: Meal; ids: string[]; status: Mark; overwrite: boolean }
type DeliveryFilter = 'all' | 'pending' | 'delivered' | 'missed';

const toldKey = (day: string, meal: Meal) => `tf-told:${day}:${meal}`;
function readTold(day: string, meal: Meal): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(toldKey(day, meal)) ?? '[]') as string[]); } catch { return new Set(); }
}
function saveTold(day: string, meal: Meal, told: Set<string>): void {
  try { localStorage.setItem(toldKey(day, meal), JSON.stringify([...told])); } catch { /* private mode */ }
}

export function Delivery() {
  const { confirm, sheet: confirmSheet } = useConfirm();
  const qc = useQueryClient();
  const today = useToday();
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const [picked, setPicked] = useState<Meal | null>((params.get('meal') as Meal | null) ?? null);
  // Areas fold away once everything in them is marked; a tap on the heading overrides that.
  const [userToggled, setUserToggled] = useState<Map<string, boolean>>(new Map());
  const [menu, setMenu] = useState<{ id: string; tell?: DeliveryStatus } | null>(null);
  const [told, setTold] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<DeliveryFilter>('all');
  const [search, setSearch] = useState('');
  const inFlight = useRef(0);

  const t = today.data;
  const kitchenMeals = t?.meals ?? me.data?.kitchen.meals;
  const served = kitchenMeals ? mealsOf(kitchenMeals) : [];
  const meal: Meal | undefined = picked && served.includes(picked) ? picked : mealForTime(served);
  const day = t?.date ?? me.data?.today;
  const k = useKitchenDay(day, meal);
  const d = k.data;

  const mark = useMutation({
    mutationFn: (v: MarkVars) => put<KitchenDayDto>('/kitchen/deliveries', { day: v.day, meal: v.meal, subscriptionIds: v.ids, status: v.status, overwrite: v.overwrite }),
    onMutate: async (v) => {
      inFlight.current++;
      setSaving((s) => new Set([...s, ...v.ids]));
      const key = ['kitchen', v.day, v.meal];
      await qc.cancelQueries({ queryKey: key });
      const cur = qc.getQueryData<KitchenDayDto>(key);
      const before: Record<string, DeliveryMarkDto | undefined> = {};
      const after: Record<string, DeliveryMarkDto | undefined> = {};
      for (const id of v.ids) {
        const was = cur?.marks[id];
        before[id] = was;
        after[id] = v.status === null ? undefined : was && !v.overwrite ? was : { status: v.status, by: '', at: new Date().toISOString() };
      }
      qc.setQueryData<KitchenDayDto>(key, (x) => x && withMarks(x, after));
      return { before };
    },
    onError: (_e, v, ctx) => {
      if (ctx) qc.setQueryData<KitchenDayDto>(['kitchen', v.day, v.meal], (x) => x && withMarks(x, ctx.before));
    },
    onSuccess: (res, v) => {
      // With several taps in flight, only the last answer is the whole truth.
      if (inFlight.current === 1) qc.setQueryData(['kitchen', v.day, v.meal], res);
    },
    onSettled: (_r, e, v) => {
      inFlight.current--;
      setSaving((s) => { const n = new Set(s); for (const id of v.ids) n.delete(id); return n; });
      if (inFlight.current === 0 && e) void qc.invalidateQueries({ queryKey: ['kitchen', v.day, v.meal] });
      void qc.invalidateQueries({ queryKey: ['today'] });
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['subscribers'] });
    },
  });

  const locked = k.isPlaceholderData;
  useEffect(() => { setUserToggled(new Map()); }, [day, meal]);
  useEffect(() => { if (d) setTold(readTold(d.day, d.meal)); }, [d?.day, d?.meal]);

  // The meal stays in the address, so a refresh or going back keeps it.
  const onSelectMeal = (m: Meal) => {
    setPicked(m);
    setParams((p) => { const n = new URLSearchParams(p); n.set('meal', m); return n; }, { replace: true });
  };
  const setMark = (ids: string[], status: Mark, overwrite = true) => {
    if (d && !locked) mark.mutate({ day: d.day, meal: d.meal, ids, status, overwrite });
  };
  const closeArea = (name: string) => setUserToggled((m) => new Map(m).set(name, false));
  const closeMenu = useCallback(() => setMenu(null), []);
  const menuFor = d && menu ? d.sheet.areas.flatMap((a) => a.customers).find((c) => c.subscriptionId === menu.id) : undefined;
  const kitchenName = me.data?.kitchen.name ?? '';

  /** The WhatsApp message for a mark, once there is one to tell. */
  const tellFor = (c: KitchenCustomer, status: DeliveryStatus) => {
    if (!d || !t || !c.phone) return null;
    const text = deliveryUpdateMessage({ kitchenName, customerName: c.name, meal: d.meal, status, day: d.day, today: t.date });
    return { text, link: waLink(c.phone, text), done: told.has(`${c.subscriptionId}:${status}`) };
  };
  const markTold = (id: string, status: DeliveryStatus) => {
    if (!d) return;
    const next = new Set(told).add(`${id}:${status}`);
    setTold(next);
    saveTold(d.day, d.meal, next);
  };

  const anyMarked = d ? d.progress.delivered + d.progress.notHome + d.progress.failed > 0 : false;
  const misses = d ? d.progress.notHome + d.progress.failed : 0;
  const stops = d ? d.progress.delivered + misses + d.progress.pending : 0;
  const query = search.trim().toLowerCase();
  const digits = query.replace(/\D/g, '');

  const areas = useMemo(() => {
    if (!d) return [];
    return d.sheet.areas.map((a) => {
      const status = (id: string) => d.marks[id]?.status ?? null;
      const customers = a.customers.filter((c) => {
        const s = status(c.subscriptionId);
        if (filter === 'pending' && s !== null) return false;
        if (filter === 'delivered' && s !== 'delivered') return false;
        if (filter === 'missed' && s !== 'not_home' && s !== 'failed') return false;
        return !query
          || c.name.toLowerCase().includes(query)
          || (digits.length >= 3 && !!c.phone?.includes(digits))
          || !!c.note?.toLowerCase().includes(query)
          || a.name.toLowerCase().includes(query);
      });
      return { ...a, customers, total: a.customers.length, left: a.customers.filter((c) => !d.marks[c.subscriptionId]).length };
    }).filter((a) => a.customers.length > 0);
  }, [d, filter, query, digits]);

  return (
    <>
      <main className="main">
        <PageTitle title="Delivery run" sub={day ? `Today · ${formatDay(day)}` : <Skel w={150} />} />

        {served.length > 1 && meal && (
          <Segmented
            label="Meal"
            value={meal}
            onChange={onSelectMeal}
            options={served.map((m) => ({
              value: m,
              label: <span className="seg-tab-content">{mealName(m)}{d && <span className="seg-tab-badge">{d.totals[m]}</span>}</span>,
            }))}
          />
        )}

        {k.isError && !d ? (
          <LoadError what="the delivery run" onRetry={() => void k.refetch()} />
        ) : !meal || !d ? (
          <PageLoader label="Loading the delivery run" />
        ) : !d.isDeliveryDay ? (
          <Card><Empty title="No deliveries today">Not a delivery day, or a holiday.</Empty></Card>
        ) : (
          <>
            <Hero label={`Today’s ${meal}`}>
              <Mono muted>Today · {mealName(meal)}</Mono>
              <div className="number-row">
                <span className="big">{d.sheet.total}</span>
                <span><b>{mealCount(meal, d.sheet.total).split(' ')[1]}</b><small>to deliver today</small></span>
              </div>
              {d.sheet.total > 0 && (
                <div className="food-facts">
                  <span><FoodMark food="nonveg" plate />{d.sheet.byFood.nonveg} non-veg</span>
                  <span><FoodMark food="veg" plate />{d.sheet.byFood.veg} veg</span>
                  {d.sheet.byFood.egg > 0 && <span><FoodMark food="egg" plate />{d.sheet.byFood.egg} egg</span>}
                </div>
              )}
              {d.sheet.total > 0 && <Progress p={d.progress} />}
            </Hero>

            {d.sheet.total > 0 && (
              <div className="stack cards">
                <SearchField value={search} onChange={setSearch} label="Search deliveries" placeholder="Search name, phone or area" />
                <Chips label="Filter deliveries" scroll>
                  <Chip on={filter === 'all'} count={stops} onClick={() => setFilter('all')}>All</Chip>
                  <Chip on={filter === 'pending'} count={d.progress.pending} onClick={() => setFilter('pending')}>To deliver</Chip>
                  <Chip on={filter === 'delivered'} count={d.progress.delivered} onClick={() => setFilter('delivered')}>Delivered</Chip>
                  {(misses > 0 || filter === 'missed') && <Chip on={filter === 'missed'} count={misses} onClick={() => setFilter('missed')}>Missed</Chip>}
                </Chips>
              </div>
            )}

            {d.progress.pending === 0 && d.sheet.total > 0 && (
              <div className="celebrate-card">
                <div className="celebrate-icon"><Icon name="check" size={24} sw={3} /></div>
                <div>
                  <h4 className="celebrate-title">Run complete</h4>
                  <p className="celebrate-sub">Every stop on today’s {meal} run is marked.</p>
                </div>
              </div>
            )}

            {mark.error && <p className="err" role="alert">{errorMessage(mark.error)}</p>}

            {areas.length === 0 ? (
              <Card>
                <Empty title={query ? 'No one matches' : filter === 'pending' ? 'Nothing left to deliver' : filter === 'delivered' ? 'Nothing delivered yet' : 'No missed deliveries'}>
                  {query ? 'Try another name, phone number or area.' : filter === 'pending' ? 'Every stop on this run is marked.' : undefined}
                </Empty>
              </Card>
            ) : (
              <div className="stack cards">
                {areas.map((a) => {
                  const done = a.left === 0;
                  const open = userToggled.get(a.name) ?? (filter === 'delivered' || !done);
                  const pendingIds = a.customers.filter((c) => !d.marks[c.subscriptionId]).map((c) => c.subscriptionId);
                  return (
                    <FoldCard
                      key={a.name}
                      icon={<Icon name="pin" size={16} />}
                      title={a.name}
                      meta={done ? <Badge kind="ok"><Icon name="check" size={12} sw={2.8} /> Done</Badge> : <span className="group-head-badge">{a.left} left</span>}
                      open={open}
                      onToggle={() => setUserToggled((m) => new Map(m).set(a.name, !open))}
                    >
                      {pendingIds.length > 0 && (
                        <div className="mark-all">
                          <BtnSecondary
                            sm
                            icon="check"
                            label={pendingIds.length === a.total ? `Mark all ${a.total} delivered` : `Mark ${pendingIds.length} more delivered`}
                            disabled={locked}
                            onClick={() => { if (pendingIds.length === a.left) closeArea(a.name); setMark(pendingIds, 'delivered', false); }}
                          />
                        </div>
                      )}
                      {a.customers.map((c) => {
                        const status = d.marks[c.subscriptionId]?.status ?? null;
                        const busy = saving.has(c.subscriptionId);
                        const tell = status ? tellFor(c, status) : null;
                        const label = tiffinsLabel(c.tiffins);
                        return (
                          <div className={`eat mark-row can${status === 'delivered' ? ' is-delivered' : ''}`} key={c.subscriptionId}>
                            <span className="row eat-marks">{[...new Set(c.tiffins.map((x) => x.food))].map((f) => <FoodMark key={f} food={f} />)}</span>
                            <span className="grow">
                              <span className="mark-name">
                                <b>{c.name}</b>
                                {c.portions > 1 && <Badge>×{c.portions}</Badge>}
                                {label && <Badge kind={c.tiffins.some((x) => x.custom) ? 'saffron' : undefined}>{label}</Badge>}
                                {c.lastDay && <Badge kind="end">Last day</Badge>}
                                {c.movedFrom && c.movedFrom.length > 0 && <Badge>Moved</Badge>}
                              </span>
                              {c.note && <small className="mark-note">{c.note}</small>}
                              <span className="mark-line">
                                {status ? <Badge kind={STATUS[status].badge}>{STATUS[status].label}</Badge> : <small>Not marked</small>}
                                {status === 'failed' && <small>owed at the end of the plan</small>}
                              </span>
                            </span>
                            <div className="mark-actions">
                              {c.phone && (
                                <a href={`tel:${c.phone}`} className="mark-call" aria-label={`Call ${c.name}`}>
                                  <span><Icon name="phone" size={17} sw={2.2} /></span>
                                </a>
                              )}
                              {tell && status && (
                                <a href={tell.link} target="_blank" rel="noreferrer" className={`mark-tell${tell.done ? ' done' : ''}`} aria-label={`Tell ${c.name} on WhatsApp`} onClick={() => markTold(c.subscriptionId, status)}>
                                  <span><Icon name="chat" size={17} /></span>
                                  {tell.done && <i aria-label="Already told"><Icon name="check" size={10} sw={3} /></i>}
                                </a>
                              )}
                              <button
                                type="button"
                                className="mark-tap"
                                aria-pressed={status === 'delivered'}
                                aria-label={status === 'delivered' ? `Clear ${c.name}` : `Mark ${c.name} delivered`}
                                disabled={locked || busy}
                                aria-busy={busy || undefined}
                                onClick={() => {
                                  const next = status === 'delivered' ? null : 'delivered';
                                  if (next && a.left <= 1 && !status) closeArea(a.name);
                                  setMark([c.subscriptionId], next);
                                }}
                              >
                                <span className={`mark-dot ${status ?? 'none'}`} aria-hidden="true">
                                  {status && <Icon name={STATUS[status].icon} size={16} sw={2.8} />}
                                </span>
                              </button>
                              <button type="button" className="mark-more" aria-label={`More delivery options for ${c.name}`} disabled={locked || busy} onClick={() => setMenu({ id: c.subscriptionId })}>
                                <Icon name="sliders" size={18} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </FoldCard>
                  );
                })}
              </div>
            )}

            {anyMarked && (
              <div className="cta-end">
                <BtnSecondary
                  sm
                  danger
                  icon="refresh"
                  label={`Clear every mark for today’s ${meal}`}
                  onClick={async () => {
                    if (await confirm({ title: `Clear every mark for today’s ${meal}?`, body: 'Every stop goes back to not marked.', yes: 'Clear marks', danger: true })) {
                      setUserToggled(new Map());
                      setMark(d.sheet.areas.flatMap((a) => a.customers.map((c) => c.subscriptionId)), null);
                    }
                  }}
                />
                {confirmSheet}
              </div>
            )}
          </>
        )}
      </main>

      {menu && menuFor && d && (
        <MarkSheet
          name={menuFor.name}
          status={d.marks[menuFor.subscriptionId]?.status ?? null}
          tell={menu.tell ? tellFor(menuFor, menu.tell) : null}
          onPick={(s) => {
            const id = menuFor.subscriptionId;
            setMark([id], s);
            if (s && menuFor.phone) setMenu({ id, tell: s });
            else closeMenu();
          }}
          onTold={() => {
            if (menu.tell) markTold(menuFor.subscriptionId, menu.tell);
            closeMenu();
          }}
          onClose={closeMenu}
        />
      )}

      <BottomNav />
    </>
  );
}

const STATUS: Record<DeliveryStatus, { label: string; icon: IconName; badge: 'ok' | 'due' | 'end' }> = {
  delivered: { label: 'Delivered', icon: 'check', badge: 'ok' },
  not_home: { label: 'Not home', icon: 'home', badge: 'due' },
  failed: { label: 'Couldn’t deliver', icon: 'close', badge: 'end' },
};

function Progress({ p }: { p: KitchenDayDto['progress'] }) {
  const listed = p.delivered + p.notHome + p.failed + p.pending;
  const pct = (n: number) => `${listed ? (n / listed) * 100 : 0}%`;
  const misses = [p.notHome > 0 && `${p.notHome} not home`, p.failed > 0 && `${p.failed} couldn’t deliver`].filter(Boolean).join(' · ');
  return (
    <div className="mark-progress">
      <div className="between">
        <b>{p.delivered} of {listed} delivered</b>
        <small>{p.pending === 0 ? 'All marked' : `${p.pending} left`}</small>
      </div>
      <div className="mark-bar" role="progressbar" aria-label="Delivered" aria-valuemin={0} aria-valuemax={listed} aria-valuenow={p.delivered}>
        <i style={{ width: pct(p.delivered) }} />
        <i className="not_home" style={{ width: pct(p.notHome) }} />
        <i className="failed" style={{ width: pct(p.failed) }} />
      </div>
      <small>{misses || 'No missed deliveries'}</small>
    </div>
  );
}

const OPTIONS: Array<{ value: Mark; label: string; sub?: string }> = [
  { value: 'delivered', label: 'Delivered' },
  { value: 'not_home', label: 'Not home', sub: 'Still counts as a meal' },
  { value: 'failed', label: 'Couldn’t deliver', sub: 'Owed at the end of the plan' },
  { value: null, label: 'Clear' },
];

function MarkSheet({ name, status, tell, onPick, onTold, onClose }: {
  name: string;
  status: Mark;
  tell: { text: string; link: string; done: boolean } | null;
  onPick: (status: Mark) => void;
  onTold: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet kicker="Delivery status" title={name} onClose={onClose}>
      <div className="sheet-opts" role="radiogroup" aria-label={`Delivery status for ${name}`}>
        {OPTIONS.map((o, i) => {
          const on = o.value !== null && o.value === status;
          return (
            <button
              key={o.label}
              type="button"
              role="radio"
              aria-checked={on}
              className={`sheet-opt${on ? ' is-selected' : ''}`}
              autoFocus={i === 0}
              disabled={o.value === null && status === null}
              onClick={() => onPick(o.value)}
            >
              <span className={`mark-dot ${o.value ?? 'none'}`} aria-hidden="true">
                {o.value && <Icon name={STATUS[o.value].icon} size={16} sw={2.8} />}
              </span>
              <span className="grow">
                <b>{o.label}</b>
                {o.sub && <small>{o.sub}</small>}
              </span>
              {on && <span className="sheet-opt-check" aria-hidden="true"><Icon name="check" size={17} sw={2.8} color="var(--saffron-deep)" /></span>}
            </button>
          );
        })}
      </div>
      {tell && (
        <div className="sheet-tell">
          <div className="sheet-tell-head"><Icon name="chat" size={15} color="var(--ink-2)" /><span>Tell {name} on WhatsApp</span></div>
          <p className="sheet-bubble"><WhatsAppText text={tell.text} /></p>
          <BtnPrimary label="Send on WhatsApp" icon="chat" href={tell.link} onClick={onTold} />
        </div>
      )}
      <BtnSecondary label="Done" onClick={onClose} />
    </Sheet>
  );
}

function withMarks(d: KitchenDayDto, next: Record<string, DeliveryMarkDto | undefined>): KitchenDayDto {
  const marks = { ...d.marks };
  for (const [id, m] of Object.entries(next)) {
    if (m === undefined) delete marks[id]; else marks[id] = m;
  }
  let delivered = 0;
  let notHome = 0;
  let failed = 0;
  const listed = new Set(d.sheet.areas.flatMap((a) => a.customers.map((c) => c.subscriptionId)));
  for (const [id, m] of Object.entries(marks)) {
    if (!listed.has(id)) continue;
    if (m.status === 'delivered') delivered++;
    else if (m.status === 'not_home') notHome++;
    else if (m.status === 'failed') failed++;
  }
  return { ...d, marks, progress: { delivered, notHome, failed, pending: listed.size - delivered - notHome - failed } };
}
